import * as React from 'react';

import Alert from '@mui/material/Alert';
import { AlertColor, AlertPropsColorOverrides, AlertPropsVariantOverrides, Collapse, Dialog, DialogContent, IconButton, Link, SxProps, Theme } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import CloseIcon from '@mui/icons-material/Close';

export type NotificationBannerProps = {
    popupLinkText?: string,
    popup?: React.ReactNode,
    sx?: SxProps<Theme>;
    variant?: OverridableStringUnion<"filled" | "standard" | "outlined", AlertPropsVariantOverrides>
    severity?: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
    color?: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
  };
  
const defaultProps: NotificationBannerProps = {
    variant: "filled",
    severity: "info",
    popupLinkText: "CLICK HERE!"
};

const ONE_DAY_IN_MILLIS: number = 24 * 60 * 60 * 1000;
const ONE_HOUR_IN_MILLIS: number = 60 * 60 * 1000;
const NOTIFICATION_DISMISSAL_TIMEOUT_MILLIS: number = ONE_HOUR_IN_MILLIS;

export default function NotificationBanner(props: React.PropsWithChildren<NotificationBannerProps>) {
    const popupLinkText = props.popupLinkText || defaultProps.popupLinkText;
    const hasDialog: boolean = props.popup !== undefined;
    
    const [lastNotificationDismissal, setLastNotificationDismissal] = React.useState<number>(() => {
        const storedState = localStorage.getItem('lastNotificationDismissal');
        return storedState ? JSON.parse(storedState) : 0;
    });

    React.useEffect(() => {
        localStorage.setItem('lastNotificationDismissal', JSON.stringify(lastNotificationDismissal));
    }, [lastNotificationDismissal]);
    

    const [notificationOpen, setNotificationOpen] = React.useState<boolean>(false);
    const openNotification = () => {
        setNotificationOpen(true);
    }
    const closeNotification = () => {
        setNotificationOpen(false);
        const now: number = new Date().getTime();
        setLastNotificationDismissal(now);
    }

    const [popupOpen, setPopupOpen] = React.useState<boolean>(false);
    const openDialog = () => {
        setPopupOpen(true);
    };
    const closeDialog = () => {
        setPopupOpen(false);
    };
    
    React.useEffect(() => {
        setTimeout(() => {
            const now: number = new Date().getTime();
            if ((now - lastNotificationDismissal) > NOTIFICATION_DISMISSAL_TIMEOUT_MILLIS) {
                openNotification();
            }
        }, 1000);
    }, []);
    const variant = props.variant || defaultProps.variant;
    const severity = props.severity || defaultProps.severity;
    const color = props.color || severity;
    return (
        <>
        <Collapse in={notificationOpen}>
            <Alert 
                variant={variant} 
                severity={severity}
                color={color} 
                sx={props.sx}
                action={
                <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={closeNotification}
                    >
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                }
            >
                {props.children}
                { hasDialog ? 
                    <Link sx={{pl: 2}} onClick={openDialog}>{popupLinkText}</Link> 
                : <></> }
                
            </Alert>
        </Collapse>
        <Dialog onClose={closeDialog} 
              fullWidth
              scroll='paper'
              open={popupOpen}>
            <IconButton
                aria-label="close"
                onClick={closeDialog}
                sx={(theme) => ({
                position: 'absolute',
                right: 1,
                top: 1,
                color: theme.palette.grey[900],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                {props.popup}
            </DialogContent>
            </Dialog>
        </>
    );
}